import { Autocomplete, Button, DialogActions, Grid, TextField, Typography } from '@mui/material';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import { find, isEmpty, map, some } from 'lodash';
import React, { ReactElement } from 'react';
import { useCellTypeOptions } from 'utils/queryHooks/useCellTypeOptions';

import { useStainTypeOptions } from 'utils/queryHooks/uiConstantsHooks';

export interface NewIdsModalProps {
  unknownStainIds: string[];
  valuesForUnknownStainIdsStateHandlers: [
    {
      [stainId: string]: string;
    },
    React.Dispatch<
      React.SetStateAction<{
        [stainId: string]: string;
      }>
    >
  ];
  valuesForUnknownCellIdsStateHandlers: [
    {
      [cellId: string]: string;
    },
    React.Dispatch<
      React.SetStateAction<{
        [cellId: string]: string;
      }>
    >
  ];
  unknownCellIds: string[];
  onContinue: (newValuesForUnknownCellIds: {
    valuesForUnknownCellIds: { [cellId: string]: string };
    valuesForUnknownStainIds: { [stainId: string]: string };
  }) => void;
  onCancel: () => void;
}

const NewIdsModal = ({
  unknownStainIds,
  unknownCellIds,
  valuesForUnknownCellIdsStateHandlers,
  valuesForUnknownStainIdsStateHandlers,
  onContinue,
  onCancel,
}: NewIdsModalProps): ReactElement => {
  const { data: cellTypes, isLoading } = useCellTypeOptions();
  const { mifMarkerTypeOptionsWithoutDeprecated } = useStainTypeOptions();

  const [valuesForUnknownCellIds, setValuesForUnknownCellIds] = valuesForUnknownCellIdsStateHandlers;
  const [valuesForUnknownStainIds, setValuesForUnknownStainIds] = valuesForUnknownStainIdsStateHandlers;

  return (
    <Dialog
      open={true}
      onClick={(e) => e.stopPropagation()}
      aria-labelledby="responsive-dialog-title"
      data-testid="confirmation-modal"
      data-cy="confirmation-modal"
    >
      <DialogTitle id="responsive-dialog-title">Select New IDs For Unknown Markers and Cells</DialogTitle>
      <DialogContent>
        <DialogContentText>
          {!isEmpty(unknownCellIds) && (
            <Typography variant="h4" mt={2}>
              Choose new IDs for the following cells:
            </Typography>
          )}
          <Grid container spacing={2} p={2}>
            {map(unknownCellIds, (cellId) => (
              <Grid item container spacing={2} alignItems="center">
                <Grid item xs={3}>
                  <Typography> {cellId}</Typography>
                </Grid>

                <Grid item xs={6}>
                  <Autocomplete
                    size="small"
                    options={cellTypes || []}
                    getOptionLabel={(option) => option.displayName}
                    value={
                      valuesForUnknownCellIds?.[cellId]
                        ? find(cellTypes, (cell) => cell.id === valuesForUnknownCellIds[cellId]) || null
                        : null
                    }
                    renderInput={(params) => (
                      <TextField size="small" {...params} label="Select Cell Type" disabled={isLoading} />
                    )}
                    onChange={(event, newValue) => {
                      setValuesForUnknownCellIds((prev) => ({
                        ...prev,
                        [cellId]: newValue ? newValue.id : '',
                      }));
                    }}
                  />
                </Grid>
              </Grid>
            ))}
          </Grid>
        </DialogContentText>
        <DialogContentText>
          {!isEmpty(unknownStainIds) && (
            <Typography variant="h4" mt={2}>
              Choose new IDs for the following markers:
            </Typography>
          )}
          <Grid container spacing={2} p={2}>
            {map(unknownStainIds, (stainId) => (
              <Grid item container spacing={2} alignItems="center">
                <Grid item xs={3}>
                  <Typography> {stainId}</Typography>
                </Grid>

                <Grid item xs={6}>
                  <Autocomplete
                    size="small"
                    options={mifMarkerTypeOptionsWithoutDeprecated || []}
                    getOptionLabel={(option) => option.displayName}
                    value={
                      valuesForUnknownStainIds?.[stainId]
                        ? find(
                            mifMarkerTypeOptionsWithoutDeprecated,
                            (stain) => stain.id === valuesForUnknownStainIds[stainId]
                          ) || null
                        : null
                    }
                    renderInput={(params) => (
                      <TextField size="small" {...params} label="Select Marker" disabled={isLoading} />
                    )}
                    onChange={(event, newValue) => {
                      setValuesForUnknownStainIds((prev) => ({
                        ...prev,
                        [stainId]: newValue ? newValue.id : '',
                      }));
                    }}
                  />
                </Grid>
              </Grid>
            ))}
          </Grid>
        </DialogContentText>
      </DialogContent>
      <DialogActions>
        <Button
          variant="outlined"
          onClick={() => {
            onContinue({ valuesForUnknownCellIds, valuesForUnknownStainIds });
          }}
          disabled={
            // if any of the unknown ids do not have a value selected, disable continue
            (!isEmpty(unknownCellIds) &&
              some(unknownCellIds, (cellId: string) => isEmpty(valuesForUnknownCellIds?.[cellId]))) ||
            (!isEmpty(unknownStainIds) &&
              some(unknownStainIds, (stainId: string) => isEmpty(valuesForUnknownStainIds?.[stainId])))
          }
        >
          Continue
        </Button>
        <Button variant="outlined" onClick={onCancel}>
          Cancel
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default NewIdsModal;
